/**
 * Terms of Service
 *
 * Required for launch alongside PrivacyPage.tsx. Lawyer should review
 * before public announcement; this is a sensible working draft that
 * covers the actual product.
 */
import { Link } from "react-router-dom";
import Logo from "../../components/ui/Logo";

const LAST_UPDATED = "April 26, 2026";

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-white text-stone-800">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:px-3 focus:py-2 focus:bg-brand-700 focus:text-white focus:rounded-lg focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-brand-500/40 focus:ring-offset-2"
      >
        Skip to main content
      </a>
      <header className="border-b border-stone-100">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Logo className="w-8 h-8" aria-hidden="true" />
            <span className="font-bold text-stone-900">TeacherDashboard</span>
          </Link>
          <Link to="/" className="text-sm text-stone-600 hover:text-stone-900">
            Back to home
          </Link>
        </div>
      </header>
      <main id="main" tabIndex={-1} className="max-w-3xl mx-auto px-6 py-12 prose prose-stone">
        <h1>Terms of Service</h1>
        <p className="text-sm text-stone-500">Last updated: {LAST_UPDATED}</p>

        <h2>Who these terms are between</h2>
        <p>
          These terms are between you and TeacherDashboard
          ("TeacherDashboard," "we," "our"). By creating an account or
          using the service at teacherdashboard.ai or app.teacherdashboard.ai,
          you agree to these terms.
        </p>

        <h2>What the service does</h2>
        <p>
          TeacherDashboard is an AI-assisted writing tool for K-12 teachers.
          We generate draft text for report card comments, rubrics, and
          related teacher work products. The drafts are starting points
          that you, the teacher, are responsible for reviewing, editing,
          and submitting.
        </p>

        <h2>Your account</h2>
        <p>
          You sign in with Google. You are responsible for keeping your
          Google account secure. You are responsible for the content you
          generate and submit through the service. You may not share your
          account with others.
        </p>
        <p>
          You must be at least 18 years old to use TeacherDashboard, and
          you must be using it in a professional capacity (typically as a
          teacher, principal, or other educator).
        </p>

        <h2>Acceptable use</h2>
        <p>You agree not to:</p>
        <ul>
          <li>Enter student personal information beyond a first name (no last names, no contact details, no health information, no government identifiers).</li>
          <li>Use the service to generate content that is unlawful, harassing, defamatory, or violates a student's or family's privacy.</li>
          <li>Attempt to reverse-engineer the service, scrape it at scale, or use it to train competing AI models.</li>
          <li>Resell access to the service to other teachers as part of your own product.</li>
        </ul>

        <h2>Free trial and subscriptions</h2>
        <p>
          New accounts include a small number of free generations so you
          can evaluate the product. After that, continued use requires a
          Pro subscription. Subscriptions auto-renew on the cadence you
          chose at checkout (monthly, quarterly, or annual). You can
          cancel any time inside the product; cancellation takes effect at
          the end of the current paid period and you keep access until then.
        </p>
        <p>
          Pricing shown on the marketing site is current at the time of
          purchase. We may change pricing for new subscriptions; existing
          subscribers will be notified before any change applies to them.
        </p>

        <h2>Refunds</h2>
        <p>
          We offer a refund within 14 days of your first paid period if
          the product was not what you expected. After 14 days, refunds
          are at our discretion. Email hello@teacherdashboard.ai.
        </p>

        <h2>Content you generate</h2>
        <p>
          You own the comments, rubrics, and other content you generate
          through TeacherDashboard. We claim no ownership over your output.
        </p>
        <p>
          AI output is not always perfect. We do our best to ground the
          output in the inputs you provide, but you must review every
          draft before submitting it as part of an official report card.
          You are responsible for ensuring the final reported content
          accurately reflects the student.
        </p>

        <h2>Service availability</h2>
        <p>
          We aim for high availability but do not promise zero downtime.
          The underlying AI provider can have outages outside our control.
          We will not be responsible for delays caused by these outages.
        </p>

        <h2>Disclaimers and liability</h2>
        <p>
          TeacherDashboard is provided "as is." To the extent permitted by
          law, we disclaim warranties of merchantability, fitness for a
          particular purpose, and non-infringement. To the extent
          permitted by law, our total liability for claims related to the
          service is limited to the amount you have paid us in the
          twelve months preceding the claim.
        </p>

        <h2>Changes to these terms</h2>
        <p>
          We may update these terms. If we do, we will update the date
          above. If the change materially affects your rights or
          obligations, we will notify you by email at least 14 days
          before the change takes effect.
        </p>

        <h2>Governing law</h2>
        <p>
          These terms are governed by the laws of the Province of Ontario,
          Canada. Disputes will be resolved in the courts of Ontario.
        </p>

        <h2>Contact</h2>
        <p>
          Questions about these terms: <a href="mailto:hello@teacherdashboard.ai">hello@teacherdashboard.ai</a>.
        </p>

        <p className="mt-12 text-sm">
          See also: <Link to="/privacy" className="text-brand-700 underline">Privacy Policy</Link>.
        </p>
      </main>
    </div>
  );
}
